import * as grow from "./bp-grow-module.js";
import { DialogCloseEvent } from "@theme/dialog";

const ESTIMATE_ID = "bp-shipping-estimate";

async function updateEstimate() {
  const el = document.getElementById(ESTIMATE_ID);
  if (!el) return;

  const state = grow.getState();
  const zip = grow.getZipCode();

  if (state && zip) {
    renderEstimateText(el, state, zip);
    el.hidden = false;
  } else {
    el.hidden = true;
  }
}

/**
 * Renders the "Shipping estimate for {state}, {zip}" text into the given
 * element, with the zip code as a clickable/keyboard-focusable span that
 * opens the inline zip edit form.
 *
 * @param {HTMLElement} el
 * @param {string} state
 * @param {string} zip
 * @returns {void}
 */
function renderEstimateText(el, state, zip) {
  el.innerHTML = "";

  el.appendChild(document.createTextNode(`Shipping estimate for ${state}, `));

  const zipSpan = document.createElement("span");
  zipSpan.className = "bp-cart-zip-edit";
  zipSpan.textContent = zip;
  zipSpan.setAttribute("role", "button");
  zipSpan.setAttribute("tabindex", "0");
  zipSpan.setAttribute("aria-label", `Edit zip code: ${zip}`);
  zipSpan.addEventListener("click", (e) => {
    e.stopPropagation();
    renderCartZipForm(el);
  });
  zipSpan.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      renderCartZipForm(el);
    }
  });
  el.appendChild(zipSpan);
}

/**
 * Renders an inline zip code edit form into the given element, replacing
 * the "Shipping estimate for..." text. On submit, fetches grow data and
 * persists it via setLocalStorage, which triggers the onLocationChange
 * event to refresh the estimate text and recalculate cart shipment groups.
 *
 * @param {HTMLElement} el
 * @returns {void}
 */
function renderCartZipForm(el) {
  el.innerHTML = "";

  const labelEl = document.createElement("label");
  labelEl.htmlFor = "bp-cart-zip-input";
  labelEl.textContent = "Zip Code";
  labelEl.className = "visually-hidden";

  const form = document.createElement("div");
  form.className = "bp-cart-zip-form";

  const input = document.createElement("input");
  input.type = "text";
  input.id = "bp-cart-zip-input";
  input.name = "zip";
  input.placeholder = "Edit Zip Code";
  input.maxLength = 5;
  input.setAttribute("inputmode", "numeric");
  input.setAttribute("autocomplete", "postal-code");
  input.className = "bp-cart-zip-form__input";

  const submitBtn = document.createElement("button");
  submitBtn.type = "button";
  submitBtn.className = "bp-cart-zip-prompt";
  submitBtn.textContent = "Submit";

  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "bp-cart-zip-cancel";
  cancelBtn.setAttribute("aria-label", "Cancel");
  cancelBtn.innerHTML =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>' +
    '<path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>' +
    "</svg>";

  const errorMsg = document.createElement("p");
  errorMsg.className = "bp-cart-zip-form__error";
  errorMsg.style.display = "none";

  const handleCancel = (e) => {
    e.stopPropagation();
    updateEstimate();
    const zipSpan = document.querySelector(`#${ESTIMATE_ID} .bp-cart-zip-edit`);
    if (zipSpan instanceof HTMLElement) {
      zipSpan.focus();
    }
  };

  const handleSubmit = async (e) => {
    e?.stopPropagation();
    const zip = input.value.trim();

    if (!/^\d{5}$/.test(zip)) {
      errorMsg.textContent = "Please enter a valid 5-digit zip code.";
      errorMsg.style.display = "";
      input.focus();
      return;
    }

    errorMsg.style.display = "none";
    submitBtn.textContent = "Checking…";
    submitBtn.disabled = true;
    input.disabled = true;

    try {
      const growData = await grow.fetchGrowData(zip);
      grow.setLocalStorage(growData);
    } catch (e) {
      errorMsg.textContent =
        "We couldn't find that zip code. Please try again.";
      errorMsg.style.display = "";
      submitBtn.textContent = "Submit";
      submitBtn.disabled = false;
      input.disabled = false;
      input.focus();
    }
  };

  submitBtn.addEventListener("click", handleSubmit);
  cancelBtn.addEventListener("click", handleCancel);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  });

  form.append(input, submitBtn, cancelBtn);
  el.append(labelEl, form, errorMsg);
  input.focus();
}

document.addEventListener("DOMContentLoaded", () => {
  updateEstimate();
  document
    .getElementById(ESTIMATE_ID)
    ?.closest("cart-drawer-component")
    ?.addEventListener(DialogCloseEvent.eventName, updateEstimate);
});
document.addEventListener("cart:update", updateEstimate);
window.addEventListener("onLocationChange", updateEstimate);
